import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { agenteSupervisionAPI } from '../../services/api';
import { Loader2, Download, TrendingUp } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

// P13G-4 — Tendencia diaria determinista (cohorte por día de creación) por agente y sección.
export const PanelTendencias = ({ seccion, agente }) => {
  const { toast } = useToast();
  const [dias, setDias] = useState(30);
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);

  const filtros = () => ({ dias, section_id: seccion || undefined, agente: agente || undefined });

  useEffect(() => {
    setCargando(true);
    agenteSupervisionAPI.tendencias(filtros())
      .then(setData).catch(() => {}).finally(() => setCargando(false));
    /* eslint-disable-next-line */
  }, [dias, seccion, agente]);

  const exportar = async () => {
    try { await agenteSupervisionAPI.exportarTendenciaCsv(filtros());
      toast({ title: 'Tendencia exportada' }); }
    catch (e) { toast({ title: 'No se pudo exportar', variant: 'destructive' }); }
  };

  const maxIni = data ? Math.max(1, ...data.serie.map((d) => d.total.iniciadas)) : 1;

  return (
    <div className="space-y-4" data-testid="panel-tendencias">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
          <TrendingUp className="h-4 w-4 text-indigo-600" /> Tendencia diaria
        </span>
        <div className="flex gap-1" role="group" aria-label="Rango de días">
          {[7, 30, 90].map((r) => (
            <Button key={r} size="sm" variant={dias === r ? 'default' : 'outline'}
                    onClick={() => setDias(r)} data-testid={`tendencia-rango-${r}`}>
              {r} días
            </Button>
          ))}
        </div>
        <Button size="sm" variant="outline" onClick={exportar} className="ml-auto"
                data-testid="tendencia-exportar-csv">
          <Download className="h-4 w-4 mr-2" /> CSV
        </Button>
      </div>

      {cargando && (
        <div className="flex items-center text-gray-500 py-6" data-testid="tendencia-cargando">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Calculando tendencia…
        </div>
      )}

      {!cargando && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[['Iniciadas', 'iniciadas'], ['Completadas', 'completada'],
              ['Con error', 'con_error'], ['Escaladas', 'escalada'],
              ['Tasa final.', 'tasa_finalizacion']].map(([et, k]) => (
              <div key={k} className="border rounded-xl p-3 bg-white"
                   data-testid={`tendencia-acum-${k}`}>
                <p className="text-xs text-gray-500">{et}</p>
                <p className="text-xl font-semibold">
                  {k === 'tasa_finalizacion' ? `${data.acumulado[k]}%` : data.acumulado[k]}
                </p>
              </div>
            ))}
          </div>

          {data.vacio ? (
            <Card><CardContent className="py-10 text-center text-gray-400"
                              data-testid="tendencia-vacio">
              Sin sesiones en el rango seleccionado. Prueba otro rango o sección.
            </CardContent></Card>
          ) : (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  Serie diaria · {data.dias} días{agente ? ` · ${data.titulos[agente]}` : ''}
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="tendencia-tabla">
                  <thead className="text-left text-gray-500 border-b">
                    <tr>{['Fecha', 'Actividad', 'Inic.', 'Compl.', 'Error', 'Escal.', 'Tasa']
                      .map((c) => <th key={c} className="py-2 pr-3 font-medium">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {data.serie.map((d) => (
                      <tr key={d.fecha} className="border-b last:border-0">
                        <td className="py-1.5 pr-3 whitespace-nowrap">{d.fecha}</td>
                        <td className="py-1.5 pr-3 w-40">
                          <div className="h-2 rounded bg-indigo-500"
                               style={{ width: `${(d.total.iniciadas / maxIni) * 100}%`,
                                        minWidth: d.total.iniciadas ? '4px' : '0' }} />
                        </td>
                        <td className="py-1.5 pr-3">{d.total.iniciadas}</td>
                        <td className="py-1.5 pr-3 text-emerald-700">{d.total.completada}</td>
                        <td className={`py-1.5 pr-3 ${d.total.con_error ? 'text-red-600' : ''}`}>
                          {d.total.con_error}</td>
                        <td className="py-1.5 pr-3 text-purple-700">{d.total.escalada}</td>
                        <td className="py-1.5 pr-3">{d.total.tasa_finalizacion}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default PanelTendencias;
